/**
 * Live stream clock.
 *
 * The stream stamps a sim tick on every data frame, so the replica re-anchors
 * the timeline ~10x per second. Extrapolating from each anchor at the requested
 * `simSpeed` overshoots, because the DES rarely delivers the speed that was
 * asked for (a requested 10x measured 8.4x), and every re-anchor then yanked
 * the timeline back by a couple of sim seconds — the vehicles jittered like a
 * mispredicted netcode entity. Instead we measure the delivered rate and trim
 * the local rate to converge on the server estimate.
 */

/** Sim seconds kept behind the server estimate, absorbing per-frame tick jitter. */
export const LIVE_RENDER_DELAY_S = 1;
/**
 * Forward error beyond which the clock snaps instead of easing.
 *
 * Only a discontinuity should snap. Ordinary drift — a DES delivering half the
 * speed it was asked for, or a tick that stalls and then bursts — has to be
 * absorbed by bending the rate instead, because a snap advances every vehicle
 * along its current leg in a single frame. On a short bay hop that reads as a
 * stutter, but on a 160 m interbay chord it is a teleport across the fab.
 */
export const LIVE_HARD_RESYNC_S = 30;
/**
 * Backward error beyond which the clock rewinds.
 *
 * Rewinding is the expensive direction: `liveOhtScheduler` reads a backward
 * global clock as a seek and discards every vehicle's accumulated wall-hold
 * offset, which shoves each OHT forward on its own timeline on top of whatever
 * the clock itself did. Reserve it for a genuine restart re-anchoring near
 * zero; merely running ahead of the server is corrected by holding still.
 */
export const LIVE_HARD_REWIND_S = 120;
/** Remaining error is eased out over roughly this many sim seconds. */
const CORRECTION_WINDOW_S = 2;
/** Slowing to a stop is allowed; rewinding is not. */
const MIN_RATE_SCALE = 0;
/**
 * Catch-up ceiling. It has to exceed the error the render delay tolerates,
 * or a backlog can never be worked off by easing and every correction ends up
 * at the snap instead.
 */
const MAX_RATE_SCALE = 2;
/** Quantised ticks make short samples meaningless. */
const RATE_SAMPLE_MIN_WALL_S = 1.5;
/** Weight of each new rate sample against the running estimate. */
const RATE_SAMPLE_WEIGHT = 0.4;

export type LiveRateWindow = {
  /** Delivered sim seconds per wall second. */
  observedRate: number;
  /** Sim time at the start of the open sample window. */
  sampleS: number;
  /** Wall clock (performance.now) at the start of the open sample window. */
  sampleWallMs: number;
  /** False while `observedRate` is still the requested speed rather than a measurement. */
  measured: boolean;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function createLiveRateWindow(
  simTimeS: number,
  wallMs: number,
  requestedSimSpeed: number
): LiveRateWindow {
  const rate = Number.isFinite(requestedSimSpeed) && requestedSimSpeed > 0 ? requestedSimSpeed : 1;
  return { observedRate: rate, sampleS: simTimeS, sampleWallMs: wallMs, measured: false };
}

/**
 * Fold a fresh server tick into the delivered-rate estimate. Samples shorter
 * than `RATE_SAMPLE_MIN_WALL_S` leave the window open so the estimate is not
 * driven by one-second tick quantisation.
 */
export function measureLiveSimRate(
  window: LiveRateWindow,
  simTimeS: number,
  wallMs: number,
  requestedSimSpeed: number
): LiveRateWindow {
  const elapsedWallS = (wallMs - window.sampleWallMs) / 1000;
  if (!Number.isFinite(elapsedWallS) || elapsedWallS < RATE_SAMPLE_MIN_WALL_S) return window;

  const measured = (simTimeS - window.sampleS) / elapsedWallS;
  if (!Number.isFinite(measured) || measured <= 0) {
    return { ...window, sampleS: simTimeS, sampleWallMs: wallMs };
  }

  const ceiling = (Number.isFinite(requestedSimSpeed) && requestedSimSpeed > 0
    ? requestedSimSpeed
    : measured) * 1.5;
  // `simSpeed` is what the run asked for, not what the DES delivers, and the two
  // can differ by 2x when the process is hosting more than one stream. Easing
  // away from that seed spends the first seconds of every stream running the
  // timeline at roughly twice the rate data is arriving, which is a backlog the
  // clock then has to correct. Adopt the first real sample outright instead.
  const blended = window.measured
    ? window.observedRate + (measured - window.observedRate) * RATE_SAMPLE_WEIGHT
    : measured;
  return {
    observedRate: clamp(blended, 0.01, ceiling),
    sampleS: simTimeS,
    sampleWallMs: wallMs,
    measured: true,
  };
}

/** Where the server sim clock should be right now, extrapolated at the delivered rate. */
export function liveServerTimeS(
  anchorS: number,
  anchorWallMs: number,
  nowMs: number,
  rate: number
): number {
  const elapsedS = Math.max(0, nowMs - anchorWallMs) / 1000;
  return anchorS + elapsedS * rate;
}

export type NextLiveClockArgs = {
  /** Sim time currently on screen. */
  currentS: number;
  /** Server estimate for this instant, from `liveServerTimeS`. */
  serverS: number;
  /** Wall seconds since the previous tick. */
  wallDeltaS: number;
  /** Delivered sim seconds per wall second. */
  rate: number;
};

/**
 * Advance the on-screen clock toward the server estimate without rewinding.
 * Small errors bend the local rate; only a large error (reconnect, restart,
 * long stall) is allowed to snap the timeline.
 */
export function nextLiveClockS({
  currentS,
  serverS,
  wallDeltaS,
  rate,
}: NextLiveClockArgs): number {
  const targetS = serverS - LIVE_RENDER_DELAY_S;
  if (!Number.isFinite(currentS)) return Math.max(0, targetS);

  const errorS = targetS - currentS;
  if (errorS > LIVE_HARD_RESYNC_S || errorS < -LIVE_HARD_REWIND_S) {
    return Math.max(0, targetS);
  }
  if (!(wallDeltaS > 0)) return currentS;

  const scale = clamp(1 + errorS / CORRECTION_WINDOW_S, MIN_RATE_SCALE, MAX_RATE_SCALE);
  return currentS + wallDeltaS * rate * scale;
}
